// QR + Sponsor context backed by the Makrolog backend proxy.
//
// URL contract:
//   ?id=<qrId>            → location + sponsor (from resolver)
//   ?sponsor=<permId>     → branding only (case C)
//   ?id=<qrId>&sponsor=X  → location + branding, resolver skipped (case D)
//
// Exposes:
//   qrId               – raw id, or null
//   sponsor            – Perm-ID from URL, or null
//   loading            – true while backend calls are in-flight
//   exists             – null | true | false (only meaningful when qrId is set)
//   custom             – { title, location, aed } when qrId object exists
//   brandingLogoUrl    – absolute URL of the sponsor's logo, or null
//   brandingWarning    – true when resolver lookup failed (only relevant in
//                        case B, i.e. ?id= without ?sponsor=)
//   createObject({ title, location, locationGps, aed })
//   logScan({ demoMode, page })

import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const PERM_ID_STRICT_RE = /^[A-Za-z0-9]{10}$/;

const QrContext = createContext({
    qrId: null,
    sponsor: null,
    loading: false,
    exists: null,
    custom: null,
    brandingLogoUrl: null,
    brandingWarning: false,
});

function readFromUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        const rawId = params.get("id");
        const rawSponsor = params.get("sponsor");
        return {
            qrId: rawId && ID_RE.test(rawId) ? rawId : null,
            sponsor:
                rawSponsor && PERM_ID_STRICT_RE.test(rawSponsor)
                    ? rawSponsor
                    : null,
        };
    } catch {
        return { qrId: null, sponsor: null };
    }
}

export const QrProvider = ({ children }) => {
    const [{ qrId, sponsor }] = useState(readFromUrl);
    const [custom, setCustom] = useState(null);
    const [brandingLogoUrl, setBrandingLogoUrl] = useState(null);
    const [brandingWarning, setBrandingWarning] = useState(false);
    const [exists, setExists] = useState(qrId ? null : false);
    const [loading, setLoading] = useState(Boolean(qrId || sponsor));

    useEffect(() => {
        let cancelled = false;
        const finish = () => !cancelled && setLoading(false);

        if (qrId) {
            // Case B or D – backend handles resolver + sponsor branding.
            const url = `${API}/qr-check/${encodeURIComponent(qrId)}${
                sponsor ? `?sponsor=${encodeURIComponent(sponsor)}` : ""
            }`;
            axios
                .get(url)
                .then((res) => {
                    if (cancelled) return;
                    const d = res.data || {};
                    setExists(Boolean(d.exists));
                    setCustom(
                        d.exists
                            ? {
                                  title: d.title || "",
                                  location: d.location || "",
                                  aed: d.aed || "",
                                  number: d.number || "",
                              }
                            : null,
                    );
                    setBrandingLogoUrl(
                        d.brandingLogoUrl
                            ? `${process.env.REACT_APP_BACKEND_URL}${d.brandingLogoUrl}`
                            : null,
                    );
                    setBrandingWarning(Boolean(d.brandingWarning));
                })
                .catch(() => {
                    if (cancelled) return;
                    setExists(null);
                })
                .finally(finish);
        } else if (sponsor) {
            // Case C – branding only, no location.
            axios
                .get(`${API}/qr-branding-info/${encodeURIComponent(sponsor)}`)
                .then((res) => {
                    if (cancelled) return;
                    const d = res.data || {};
                    setBrandingLogoUrl(
                        d.brandingLogoUrl
                            ? `${process.env.REACT_APP_BACKEND_URL}${d.brandingLogoUrl}`
                            : null,
                    );
                    setExists(false);
                })
                .catch(() => {})
                .finally(finish);
        } else {
            finish();
        }

        return () => {
            cancelled = true;
        };
    }, [qrId, sponsor]);

    const createObject = async ({
        title,
        location,
        locationGps = false,
        aed = "",
    }) => {
        if (!qrId) throw new Error("no qrId");
        const res = await axios.post(
            `${API}/qr-create/${encodeURIComponent(qrId)}`,
            {
                title,
                location: location || "",
                locationGps: Boolean(locationGps),
                aed: aed || "",
            },
        );
        return res.data;
    };

    const logScan = ({ demoMode = false, page = "" } = {}) => {
        if (!qrId || !exists) return;
        axios
            .post(`${API}/qr-scan/${encodeURIComponent(qrId)}`, {
                event: "scan",
                userAgent: navigator.userAgent,
                locale: navigator.language,
                referrer: document.referrer || "",
                demoMode,
                page,
            })
            .catch(() => {});
    };

    return (
        <QrContext.Provider
            value={{
                qrId,
                sponsor,
                loading,
                exists,
                custom,
                brandingLogoUrl,
                brandingWarning,
                // Legacy alias so components that still reference logoUrl
                // keep working while we transition (removed once every
                // consumer uses brandingLogoUrl).
                logoUrl: brandingLogoUrl,
                createObject,
                logScan,
            }}
        >
            {children}
        </QrContext.Provider>
    );
};

export const useQr = () => useContext(QrContext);
